<template>
  <div
    :ref="id"
    :name="name"
    :class="{ 'input-outer-container': showCancel }"
  >
    <div
      class="input-container"
      :class="isFlexible ? 'flexible-input-container' : ''"
    >
      <div class="input-inner-start">
        <Icon
          class="search-logo"
          :icon="csgSearch"
        />
      </div>
      <input
        v-model="searchValue"
        aria-label="search input"
        class="input-search"
        :class="endPaddingClass"
        :name="name"
        :aria-description="name"
        :placeholder="placeholder"
        type="text"
        :style="style"
        :list="list"
        :maxlength="maxLength"
        @click="handleOnClick"
        @focus="onFocus"
        @input="onInput"
        @keypress="emitEvents('onKeypress')"
        @keyup="emitEvents('onKeyup')"
        @keypress.enter="emitEvents('onEnter')"
        @blur="onBlurField"
      >
      <div class="input-inner-end">
        <button
          v-if="showIcon && !isFlexible"
          aria-label="clear search"
          type="button"
          :class="['clear-icon', { 'shift-left': showText }]"
          @click.prevent="onClear"
          @blur="onBlurClear"
        >
          <Icon :icon="csgClear" />
        </button>
        <!-- TODO: Create slot for icon to be added here -->
        <button
          v-else-if="icon && !showIcon"
          aria-label="Search"
          :class="icon"
          type="button"
          @click.prevent="emitEvents('onIconClick')"
        />
        <button
          v-if="showText"
          class="input-inline-cta"
          type="button"
          @click.prevent="emitEvents('onIconClick')"
        >
          <div class="input-inline-cta-fake">
            Go
          </div>
          <div class="input-inline-cta-content">
            Go
          </div>
        </button>
      </div>
    </div>
    <div
      v-if="showCancel"
      class="input-outer-button"
    >
      <button
        type="button"
        @click="onCancel"
      >
        Cancel
      </button>
    </div>
  </div>
  <template v-if="isComboBox">
    <ComboBox
      :width="`${searchWidth}px`"
      :data="searchHighlightsArray"
      :combo-box-type="comboBoxType"
      @selectedResult="selectedResult"
      @clearSearchResults="emitEvents('clearSearchResults')"
    />
  </template>
</template>
<script>
import { csgClear, csgSearch } from '@csgSvgIcons/icons';
import generateRandomId from '../../utils/randomNumber';
import Icon from '../icon';
import ComboBox from '../comboBox/ComboBox';

export default {
  name: 'Search',
  components: {
    Icon,
    ComboBox,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    id: {
      type: [String, Number],
      default: () => generateRandomId(),
    },
    name: {
      type: String,
      default: 'search',
    },
    icon: {
      type: String,
      default: '',
    },
    showText: {
      type: Boolean,
      default: false,
    },
    showCancel: {
      type: Boolean,
      default: false,
    },
    isFlexible: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Search',
    },
    list: {
      type: String,
      default: '',
    },
    maxLength: {
      type: [String, Number],
      default: undefined,
    },
    comboBoxType: {
      type: String,
      default: undefined,
      validator: (val) => ['location', 'searchHistory'].includes(val),
    },
    comboBoxData: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['onCancel', 'onInputClick', 'onIconClick', 'onEnter', 'onClear', 'onKeypress', 'onKeyup', 'onFocus', 'onBlur', 'update:modelValue', 'selectedResult', 'clearSearchResults'],
  data() {
    return {
      searchValue: this.modelValue,
      showIcon: !!this.modelValue,
      style: {},
      csgClear,
      csgSearch,
      isComboBox: false,
      searchHighlightsArray: this.comboBoxData,
      searchWidth: null,
    };
  },
  computed: {
    endPaddingClass() {
      if (this.isFlexible) {
        return 'padding-end-search-flexible';
      }
      if (this.showText) {
        return this.showIcon ? 'padding-end-search-icon-cta' : 'padding-end-search-cta';
      }
      if (this.icon) {
        return 'padding-end-search-icon';
      }
      return this.showIcon ? 'padding-end-search-icon' : '';
    },
  },
  watch: {
    modelValue(newValue, oldValue) {
      // Prevents infinite loop
      if (newValue === oldValue) {
        return;
      }
      this.searchValue = newValue;
      this.showIcon = !!newValue;
    },
    searchValue(newValue) {
      // Prevents infinite loop
      if (this.modelValue === newValue) {
        return;
      }
      this.$emit('update:modelValue', newValue);
    },
    comboBoxData(newValueArray) {
      this.searchHighlightsArray = [...newValueArray];
      this.isComboBox = this.searchValue.trim().length > 0 && this.searchHighlightsArray.length > 0 && this.showComboBox();
    },
  },
  mounted() {
    if (this.isFlexible) {
      this.resizeSearch();
    }
    if (this.showComboBox()) {
      this.handleResize();
      document.addEventListener('click', this.handleOutsideClick);
    }
  },
  unmounted() {
    if (this.showComboBox()) {
      document.removeEventListener('click', this.handleOutsideClick);
      this.ro.unobserve(document.body);
    }
  },
  methods: {
    handleResize() {
      this.ro = new ResizeObserver(this.getSearchWidth);
      this.ro.observe(document.body);
    },
    getSearchWidth() {
      this.searchWidth = this.$refs[this.id]?.clientWidth;
    },
    onBlurField(event) {
      if (event.relatedTarget?.className !== 'clear-icon') {
        this.hide();
      }
      if (this.isFlexible) {
        this.resizeSearch(event);
      }

      this.emitEvents('onBlur');
    },
    onBlurClear(event) {
      const inputElement = this.$refs[this.id];
      const isClickedInsideInputElement = inputElement.contains(event.relatedTarget);
      if (!isClickedInsideInputElement) {
        this.hide();
      }
    },
    resizeSearch(event) {
      if (event && (event.type === 'focus' || event.type === 'input')) {
        this.style.width = `${this.searchValue.length + 10}ch`;
      } else if (this.searchValue.length === 0) {
        this.style.width = '16ch';
      }
    },
    onCancel() {
      this.searchValue = '';
      this.emitEvents('onCancel');
      if (this.isFlexible) {
        this.resizeSearch();
      }
    },
    onClear() {
      this.searchValue = '';
      this.showIcon = false;
      this.emitEvents('onClear');
    },
    emitEvents(event) {
      this.$emit(event, this.searchValue);
    },
    onInput(event) {
      if (this.searchValue.length > 0) {
        this.showIcon = true;
      } else if (this.searchValue.length === 0) {
        this.showIcon = false;
      }
      if (this.isFlexible) {
        this.resizeSearch(event);
      }
    },
    onFocus(event) {
      if (this.searchValue.length > 0) {
        this.showIcon = true;
      }
      if (this.isFlexible) {
        this.resizeSearch(event);
      }

      this.emitEvents('onFocus');
    },
    hide() {
      if (this.icon || this.searchValue.length === 0) {
        this.showIcon = false;
      }
    },
    selectedResult(value) {
      this.searchValue = value.description;
      this.isComboBox = false;
      this.$emit('selectedResult', value);
    },
    handleOnClick() {
      this.isComboBox = this.showComboBox() && this.searchHighlightsArray.length > 0;
      this.emitEvents('onInputClick');
    },
    handleOutsideClick(event) {
      if (this.$refs[this.id] && !this.$refs[this.id].contains(event.target)) this.isComboBox = false;
    },
    showComboBox() {
      return this.comboBoxType === 'location' || this.comboBoxType === 'searchHistory';
    },
  },
};
</script>
<style lang="scss" scoped>
@import "~@core/csg/src/scss/forms/forms.scss";
@include csg-forms;

.flexible-input-container {
  width: auto;
}

.input-search::-webkit-calendar-picker-indicator {
  display: none !important;
}

.input-inline-cta {
  // TODO background-color & color should be changed in CSG and removed from here
  background-color: black;
  color: white;
  height: 40px;
  margin-right: -8px;

  .input-inline-cta-fake {
    visibility: hidden;
  }

  .input-inline-cta-content {
    position: absolute;
    top: 10px;
    left: 10px;
  }
}

.clear-icon.shift-left {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -29px;
  margin: auto;
}
</style>

<style lang="scss" brand="mcom" scoped>
.clear-icon {
  cursor: pointer;
}

.input-inline-cta {
  border-radius: 0 50% 50% 0;
}
</style>

<style lang="scss" brand="bcom" scoped>
.input-inline-cta .input-inline-cta-content {
  left: 12px;
}
</style>
